'use client';

import { ChangeEvent, ReactNode, useMemo, useState } from 'react';
import {
  FormLabelProps,
  Input as ChakraInput,
  InputProps as ChakraInputProps,
  forwardRef,
  Icon,
  useBoolean,
} from '@chakra-ui/react';
import { FieldError } from 'react-hook-form';

import { FaRegEye, FaRegEyeSlash } from 'react-icons/fa';
import { IconButton } from '../../IconButton';
import { InputWrapper, InputWrapperProps } from './InputWrapper';

interface Props extends ChakraInputProps {
  error?: FieldError;
  label?: ReactNode;
  inputWrapperProps?: InputWrapperProps;
  labelProps?: FormLabelProps;
  inputLeft?: ReactNode;
  inputRight?: ReactNode;
}

export type InputProps = Props;

export const Input = forwardRef<InputProps, 'input'>((props, ref) => {
  const {
    error,
    label,
    inputWrapperProps = {},
    labelProps = {},
    onChange,
    type,
    inputLeft,
    inputRight,
    maxW,
    ...rest
  } = props;

  const [value, setValue] = useState('');

  const [isPasswordVisible, setIsPasswordVisible] = useBoolean(false);

  const hasValue = Boolean(value) || Boolean(rest?.value);

  const isPasswordInput = type === 'password';
  const inputType = isPasswordVisible ? 'text' : type;
  const hasRightElement = Boolean(inputRight) || isPasswordInput;

  const mergedLabelProps = useMemo(() => {
    if (rest?.variant !== 'network') return labelProps;

    return {
      fontWeight: 'light',
      _focus: { color: 'dynamic.highlight' },
      ...labelProps,
    };
  }, [labelProps, rest?.variant]);

  const onChangeInput = (event: ChangeEvent<HTMLInputElement>) => {
    setValue(event.target.value);

    onChange?.(event);
  };

  const resolveRevealPasswordButton = () => {
    if (!hasValue) return null;

    return (
      <IconButton
        aria-label="toggle tooltip error"
        onClick={setIsPasswordVisible.toggle}
        variant="ghost"
      >
        <Icon
          as={isPasswordVisible ? FaRegEyeSlash : FaRegEye}
          fontSize="1.25rem"
        />
      </IconButton>
    );
  };

  return (
    <InputWrapper
      label={label}
      labelProps={mergedLabelProps}
      hasValue={hasValue}
      error={error?.message}
      size={rest?.size}
      inputLeft={inputLeft}
      maxW={maxW}
      inputRight={
        hasRightElement && (
          <>
            {inputRight}

            {isPasswordInput && resolveRevealPasswordButton()}
          </>
        )
      }
      {...inputWrapperProps}
    >
      <ChakraInput
        _focus={{ _placeholder: { visibility: 'hidden' } }}
        _placeholder={{ color: 'blue.800' }}
        ref={ref}
        onChange={onChangeInput}
        type={inputType}
        {...rest}
      />
    </InputWrapper>
  );
});
